import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Asset, updateAsset } from '@/utils/collectionsGraphQL';
import { useToast } from '@/hooks/use-toast';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';

interface EditAssetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset;
  onSuccess: () => void;
}

export const EditAssetModal: React.FC<EditAssetModalProps> = ({
  open,
  onOpenChange,
  asset,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    type: 'simple' as 'simple' | 'diff',
    page_id: '',
    slug: '',
    version: '',
    title: '',
    description: '',
    primary_image: '',
    secondary_image: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Initialize form with asset data when modal opens
  useEffect(() => {
    if (open && asset) {
      setFormData({
        type: asset.type,
        page_id: asset.page_id,
        slug: asset.slug,
        version: asset.version,
        title: asset.header || '', // Using title field name for form, but loading from header
        description: asset.subheader || '', // Using description field name for form, but loading from subheader
        primary_image: asset.primary_image || '',
        secondary_image: asset.secondary_image || ''
      });
    }
  }, [open, asset]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "Asset title is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.page_id.trim()) {
      toast({
        title: "Error",
        description: "Page ID is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.slug.trim()) {
      toast({
        title: "Error",
        description: "Slug is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.version.trim()) {
      toast({
        title: "Error",
        description: "Version is required",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const updatedAsset = await updateAsset(asset.id, {
        type: formData.type,
        page_id: formData.page_id.trim(),
        slug: formData.slug.trim(),
        version: formData.version.trim(),
        header: formData.title.trim(), // Save form title field to header
        subheader: formData.description.trim() || undefined, // Save form description field to subheader
        primary_image: formData.primary_image.trim() || undefined,
        secondary_image: formData.secondary_image.trim() || undefined
      });


      onSuccess();
    } catch (error) {
      console.error('Error updating asset:', error);
      toast({
        title: "Error",
        description: "Failed to update asset. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>
              Update the asset information.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter asset title"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleInputChange('type', value)}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">Simple</SelectItem>
                    <SelectItem value="diff">Diff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="page_id">Page ID *</Label>
                <Input
                  id="page_id"
                  value={formData.page_id}
                  onChange={(e) => handleInputChange('page_id', e.target.value)}
                  placeholder="Enter page ID"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  placeholder="Enter slug"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="version">Version *</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => handleInputChange('version', e.target.value)}
                placeholder="Enter version"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter asset description (optional)"
                rows={3}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="primary_image">Primary Image URL</Label>
              <Input
                id="primary_image"
                value={formData.primary_image}
                onChange={(e) => handleInputChange('primary_image', e.target.value)}
                placeholder="Enter primary image URL (optional)"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondary_image">Secondary Image URL</Label>
              <Input
                id="secondary_image"
                value={formData.secondary_image}
                onChange={(e) => handleInputChange('secondary_image', e.target.value)}
                placeholder="Enter secondary image URL (optional)"
                disabled={isLoading}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !formData.title.trim() || !formData.page_id.trim() || !formData.slug.trim() || !formData.version.trim()}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};